import { IonToggle } from '@ionic/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { DOC_TYPE_AADE_CODE } from '../../../interfaces/documentType.interface';
import { PATHS } from '../../../navigation/data';
import { isDocTypeStandardDeliveryNote } from '../../../services/utils/myDataUtils';
import { TooltipInInput } from '../TooltipInInput';

interface Props {
  nonObligatedRecipientField: any;
  withoutDigitalTransportTrackingField: any;
  formik: any;
}

export const TransitToggles = ({ nonObligatedRecipientField, withoutDigitalTransportTrackingField, formik }: Props) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isEdit = useMemo(() => pathname.includes(PATHS.EDIT_SALE), [pathname]);
  const isClone = useMemo(
    () => pathname.includes(PATHS.CLONE_SALE) || pathname.includes(PATHS.CORRELATE_SALE),
    [pathname],
  );
  const isNew = useMemo(() => !isEdit && !isClone, [isEdit, isClone]);

  const documentType = useMemo(
    () => formik?.values?.documentTypeId?.aadeCode || formik?.values?.documentTypeId?.object?.aadeCode,
    [formik?.values?.documentTypeId?.aadeCode, formik?.values?.documentTypeId?.object?.aadeCode],
  );
  const isMarkedAsDeliveryNote = useMemo(() => formik?.values?.isDeliveryNote, [formik?.values?.isDeliveryNote]);

  // Excludes 10.1/10.2 (Quantity Receipt Form) - a genuinely different myDATA document (a
  // receiving/inbound-quantity record, not a transit shipment), so NOR/WDTT don't apply to it.
  const isVisible = useMemo(
    () => isDocTypeStandardDeliveryNote(documentType) || isMarkedAsDeliveryNote,
    [documentType, isMarkedAsDeliveryNote],
  );

  // Edge case 1: 11.1, 11.4 with delivery note on → NOR forced true, disabled
  const isEdgeCase1 = useMemo(
    () =>
      (documentType === DOC_TYPE_AADE_CODE.RECEIPT || documentType === DOC_TYPE_AADE_CODE.RECEIPT_RETURN_RELATED) &&
      isMarkedAsDeliveryNote,
    [documentType, isMarkedAsDeliveryNote],
  );
  // Edge case 2: 1.2, 1.3 with delivery note on → at least one must be true
  const isEdgeCase2 = useMemo(
    () =>
      (documentType === DOC_TYPE_AADE_CODE.INVOICE_INTRA_COMMUNITY_DELIVERIES ||
        documentType === DOC_TYPE_AADE_CODE.INVOICE_THIRD_COUNTRIES_DELIVERIES) &&
      isMarkedAsDeliveryNote,
    [documentType, isMarkedAsDeliveryNote],
  );

  const norName = useMemo(() => nonObligatedRecipientField?.name, [nonObligatedRecipientField?.name]);
  const wdttName = useMemo(
    () => withoutDigitalTransportTrackingField?.name,
    [withoutDigitalTransportTrackingField?.name],
  );
  const norValue = useMemo(() => !!formik?.values?.[norName], [formik?.values, norName]);
  const wdttValue = useMemo(() => !!formik?.values?.[wdttName], [formik?.values, wdttName]);
  // Setting both fields via two sequential `setFieldValue` calls is a real Formik footgun: each
  // call auto-validates against `state.values` from the *current render's* closure, so the
  // second call's validation doesn't see the first call's update yet (both dispatches land
  // before React re-renders). For the "at least one" edge-case-2 rule that leaves
  // `formik.errors`/`isValid` stuck on a stale, incorrect result — the Save button stays
  // disabled even though the (correctly computed) inline error above has already cleared.
  // `setValues` with a functional updater sets both fields in one dispatch/validate pass instead.
  const setValues = formik?.setValues;

  const isNorDisabled = useMemo(() => isEdgeCase1, [isEdgeCase1]);
  const showAtLeastOneError = useMemo(() => isEdgeCase2 && !norValue && !wdttValue, [isEdgeCase2, norValue, wdttValue]);

  // On new sales, apply default NOR=true when an edge case condition becomes active
  const appliedDefaultRef = useRef<string | null>(null);
  // On edit/clone, the very first run of the effect below reflects the entity's own saved
  // values (e.g. a doc that already deliberately overrides edge case 1 via WDTT) — skip forcing
  // only for that first run so we never clobber it just by opening the doc. Any doc-type or
  // isDeliveryNote change the user makes afterward is a real action and should still react
  // normally, same as on a brand-new doc.
  const isFirstRunRef = useRef(true);

  // Reset both fields when the transit section becomes hidden
  const prevIsVisibleRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (prevIsVisibleRef.current && !isVisible) {
      setValues?.((prev: any) => ({ ...prev, [norName]: false, [wdttName]: false }));
      appliedDefaultRef.current = null;
    }
    prevIsVisibleRef.current = isVisible;
  }, [isVisible, norName, wdttName, setValues]);

  useEffect(() => {
    const isFirstRun = isFirstRunRef.current;
    isFirstRunRef.current = false;

    if (!isVisible) return;
    if (isFirstRun && !isNew) return;
    if (isEdgeCase1 || isEdgeCase2) {
      const key = `${documentType}-${String(isMarkedAsDeliveryNote)}`;
      if (appliedDefaultRef.current !== key) {
        appliedDefaultRef.current = key;
        setValues?.((prev: any) => ({ ...prev, [norName]: true, [wdttName]: false }));
      }
    } else if (appliedDefaultRef.current !== null) {
      // Leaving an edge case we previously forced NOR=true for (e.g. switching doc type from
      // 11.1 back to 1.1) — revert to the base default (both off) instead of leaving the forced
      // value stuck. No-op when we were never in an edge case to begin with, so a manual toggle
      // on a non-edge-case doc type is never clobbered.
      appliedDefaultRef.current = null;
      setValues?.((prev: any) => ({ ...prev, [norName]: false, [wdttName]: false }));
    }
  }, [isNew, isVisible, isEdgeCase1, isEdgeCase2, documentType, isMarkedAsDeliveryNote, norName, wdttName, setValues]);

  const handleNorToggle = useCallback(() => {
    if (isNorDisabled) return;
    const next = !norValue;
    setValues?.((prev: any) => ({ ...prev, [norName]: next, [wdttName]: next ? false : prev[wdttName] }));
  }, [isNorDisabled, norValue, setValues, norName, wdttName]);

  const handleWdttToggle = useCallback(() => {
    const next = !wdttValue;
    setValues?.((prev: any) => ({
      ...prev,
      [wdttName]: next,
      // Turning WDTT on always clears NOR; turning it off restores the edge-case-1 default,
      // otherwise leaves NOR as-is.
      [norName]: next ? false : isEdgeCase1 ? true : prev[norName],
    }));
  }, [wdttValue, isEdgeCase1, setValues, wdttName, norName]);

  if (!isVisible) return null;

  return (
    <div style={{ width: '100%' }}>
      <div className="normalText3 padding-l-8" style={{ marginBottom: '8px' }}>
        {t('FORMS.TRANSIT_SECTION')}
      </div>
      <div className="flex-start-center-row-wrap gap-30-16">
        <div className="width-300px">
          <div className="g-toggle-container height-50px b-field-border border-radius-input positionRelative">
            <TooltipInInput value="NON_OBLIGATED_RECIPIENT" />
            <div className="sc-ion-label-md-s normalText4 border-radius-24">{t('FORMS.NON_OBLIGATED_RECIPIENT')}</div>
            <IonToggle
              data-testid={`${norName}_${norValue}`}
              disabled={isNorDisabled}
              checked={norValue}
              mode="ios"
              className="border-radius-24"
              onClick={handleNorToggle}
            />
          </div>
        </div>
        <div className="width-300px">
          <div className="g-toggle-container height-50px b-field-border border-radius-input positionRelative">
            <TooltipInInput value="WITHOUT_DIGITAL_TRANSPORT_TRACKING" />
            <div className="sc-ion-label-md-s normalText4 border-radius-24">
              {t('FORMS.WITHOUT_DIGITAL_TRANSPORT_TRACKING')}
            </div>
            <IonToggle
              data-testid={`${wdttName}_${wdttValue}`}
              checked={wdttValue}
              mode="ios"
              className="border-radius-24"
              onClick={handleWdttToggle}
            />
          </div>
        </div>
        {showAtLeastOneError && (
          <div className="normalText3 text-danger padding-l-8" style={{ width: '100%' }}>
            {t('FORMS.ERRORS.TRANSIT_AT_LEAST_ONE')}
          </div>
        )}
      </div>
    </div>
  );
};
